import apisauce, { ApisauceInstance } from 'apisauce';

import config from '../../config';
import { ExtraUserDetails, SignupTokenData } from '../models/auth/types';
import { NullableString } from '../util/types';

import { ICommonAuthService } from './types';

/**
 * Path params marked given as $1, $2 etc.
 */
const API_PATH = {
  signupToken: '/api/v1/User/sign-up-token',
  /**
   * Has 3 functions:
   *  - creates a user (new record in Users table)
   *  - gets first name, last name and country of residence as input (to create a new record in Individuals table)
   *  - creates the user for both 10n Web App and Mobile App, no matter which was used to register
   * This endpoint has the following implementations:
   *  1. /api/v1/User - the original one
   *  2. /api/v1/User/with-sending-details - was added to enable country of residence (see CRM.service.ts `/country/list`)
   *  3. /api/v1/User/bulk-insert-clients - was added to extend 2. - when used it also adds all 10n clients to the account,
   *     so that a 10n account registered in 10n Mobile App would also be available for 10n Web App.
   * Only the last number 3. is to be used with 10n apps as it has all needed in one package.
   * */
  registerUserWithAllClients: '/api/v1/User/bulk-insert-clients',
  confirmEmail: '/api/v1/User/confirm-email',
  resendEmail: '/api/v1/User/send-confirmation-email',
  /**
   * @deprecated Integrated before but there is no usage in WEB and MOBILE
   */
  userPermissions: '/api/v1/User/permissions',
  phoneNumberSendCode: '/api/v1/User/phone-number/send-code',
  phoneNumberVerify: '/api/v1.1/User/phone-number/verify',
  /**
   * @deprecated Integrated before but there is no usage in WEB and MOBILE
   */
  getAvailableClients: '/api/v1/User/available-clients',
  postAssignedClients: '/api/v1/User/$1/assigned-clients/$2',
  putAssignedClients: '/api/v1/User/$1/assigned-clients',
  sendResetPassword: '/api/v1/User/send-reset-password',
  sendNonAppropriatenessEmail: '/api/v1/User/send-non-appropriateness/$1',
};

export default class CommonAuthService implements ICommonAuthService {
  private api: ApisauceInstance;
  private bffApi: ApisauceInstance;
  private phone: string | null = null;

  constructor() {
    this.api = apisauce.create({
      baseURL: `${config.auth.issuer}`,
      withCredentials: true,
      headers: { 'X-CSRF': '1' },
    });
    this.bffApi = apisauce.create({
      baseURL: `https://${config.gateway.url}/auth`,
      withCredentials: true,
      headers: { 'X-CSRF': '1' },
    });
  }

  registerUser(
    email: string,
    password: string,
    confirmPassword: string,
    userExtraDetails = ((null as any) as ExtraUserDetails),
    selectedTradingPlanId: NullableString = null,
  ) {
    const request = {
      email,
      password,
      confirm_password: confirmPassword,
      tenant_id: config.auth.tenantId,
      client: config.auth.clientId,
      redirect_url: config.auth.redirectUrlConfirmEmail,
      send_email: true,
      clients: [ 'DemoAceCapitalPortal', 'DemoAceCapitalPortalLocal' ],
      user_type_relation: 1,
      ...(userExtraDetails ?? {}),
    };

    if (selectedTradingPlanId) {
      request.trading_plan_id = selectedTradingPlanId;
    }

    return this.bffApi.post<any>(
      API_PATH.registerUserWithAllClients,
      request,
    );
  }

  confirmEmail(email: string, token: string) {
    return this.bffApi.post<any>(
      API_PATH.confirmEmail,
      {
        email,
        token,
        client: config.auth.clientId,
        redirect_url: config.auth.redirectUrlEmailConfirmed,
      },
    );
  }

  resendEmail(
    email: string,
    first_name: NullableString,
    last_name: NullableString,
    country_of_residence: NullableString,
    selectedTradingPlanId?: NullableString,
  ) {
    const request = {
      email,
      client: config.auth.clientId,
      return_url: config.auth.redirectUrlResendEmail,
      reset_link: config.auth.redirectUrlResetEmail,
      show_current_password: true,
      first_name,
      last_name,
      country_of_residence,
      trading_plan_id: selectedTradingPlanId,
    };

    if (!selectedTradingPlanId) {
      delete request.trading_plan_id;
    }

    return this.bffApi.post<any>(
      API_PATH.resendEmail,
      request,
    );
  }

  sendPhoneCode(userId: number, phone: string) {
    this.phone = phone;
    return this.bffApi.post<any>(
      API_PATH.phoneNumberSendCode,
      {
        id: userId,
        phone_number: phone,
      },
    );
  }

  verifyPhoneNumber(userId: number, code: string) {
    return this.bffApi.post<any>(
      API_PATH.phoneNumberVerify,
      {
        id: userId,
        phone_number: this.phone,
        code,
        redirect_url: config.auth.redirectUrlPhoneVerified,
        show_current_password: false,
      },
    );
  }

  /**
   * @deprecated Integrated before but there is no usage in WEB and MOBILE
   */
  getUserPermissions() {
    return this.bffApi.get<any>(API_PATH.userPermissions);
  }

  /**
   * @deprecated Integrated before but there is no usage in WEB and MOBILE
   */
  getAvailableClients() {
    return this.bffApi.get<any>(API_PATH.getAvailableClients);
  }

  sendResetPassword(email: string, client: string, returnUrl: string) {
    return this.bffApi.post(API_PATH.sendResetPassword, {
      email,
      client,
      return_url: returnUrl,
      show_current_password: true,
    });
  }

  sendNonAppropriatenessEmail(email: string) {
    return this.bffApi.get(API_PATH.sendNonAppropriatenessEmail.replace('$1', email));
  }

  public getSignupToken() {
    return this.bffApi.post<SignupTokenData>(API_PATH.signupToken);
  }

  /**
   * Exchanges an email signup token for a BFF session cookie (`__Host-bff`).
   * The BFF redeems the token at the IdP server-side; no token reaches the browser.
   * Hits the BFF root (`/bff/onboarding/redeem-signup`), not the `/auth` base used by `bffApi`,
   * so a raw `fetch` is used to send credentials and the `X-CSRF` guard and to branch on status.
   */
  public async redeemSignupToken(username: string, signupToken: string, returnUrl?: string): Promise<void> {
    const res = await fetch(config.auth.bffRedeemSignupUrl, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', 'X-CSRF': '1' },
      body: JSON.stringify({ username, signupToken, ...(returnUrl ? { returnUrl } : {}) }),
    });
    if (res.status === 204 || res.status === 200) return; // cookie set
    const error = new Error(res.status === 401 ? 'SIGNUP_TOKEN_INVALID' : 'REDEEM_FAILED');
    (error as any).status = res.status;
    throw error; // 400 / unexpected
  }
}
